import { ApiError } from "../errors/ApiError.js";
import { TokenError } from "../errors/TokenError.js";
import { TokenExpiredError } from "../errors/TokenExpiredError.js";
//import { NonJsonResponseError } from "../errors/NonJsonResponseError.js"

import { aspNetFlatten } from '../utilities.js';

class API {
	constructor(baseUrl, config) {
		if (!baseUrl) {
			throw `API config.baseUrl is required`;
		}
		this._config = config || {};
		this._config.baseUrl = baseUrl;
	}

	// Public
	GET(route, body, headers, params) {
		return this._apiFetch(route, `GET`, body, headers, params);
	}

	async GETblob(route, headers) {
		const response = await window.fetch(this._config.baseUrl + route, {
			method: `GET`,
			headers: headers || new window.Headers()
		});
		const blob = await response.blob();
		return blob;
	}

	async GETfile(route, headers) {
		const response = await window.fetch(this._config.baseUrl + route, {
			method: `GET`,
			headers: headers || new window.Headers()
		});
		const cdHeader = response.headers.get(`Content-Disposition`);
		const parts = cdHeader.split(`;`);
		const filename = parts[1].split(`=`)[1];
		const ctHeader = response.headers.get(`Content-Type`);
		const blob = await response.blob();
		return new File([blob], filename, { type: ctHeader });
	}

	POST(route, body, headers, keepalive = false) {
		return this._apiFetch(route, `POST`, body, headers, undefined, keepalive);
	}

	// Posts a js obj like a form to an API endpoint, converting the js obj to a FormData recursively
	POSTForm(route, body, headers) {
		const aspNetObj = aspNetFlatten(body);
		const formData = new FormData();
		for (const key in aspNetObj) {
			if (Object.prototype.hasOwnProperty.call(aspNetObj, key)) {
				formData.append(key, aspNetObj[key]);
			}
		}
		return this.POSTFormRaw(route, formData, headers);
	}

	// Posts FormData to an API endpoint. To convert a Js Obj to FormData, use PostForm(...)
	POSTFormRaw(route, formData, headers) {
		headers = headers || new window.Headers();
		headers.append(`Content-Type`, `multipart/form-data`);
		return this._apiFetch(route, `POST`, formData, headers);
	}

	PATCH(route, body, headers) {
		return this._apiFetch(route, `PATCH`, body, headers);
	}

	DELETE(route, body, headers) {
		return this._apiFetch(route, `DELETE`, body, headers);
	}

	PUT(route, body, headers) {
		return this._apiFetch(route, `PUT`, body, headers);
	}

	// Private
	_config;

	//standard syntax #functionName() doesn't work with AMD modules
	_apiFetch = async function (route, method, body, headers, params, keepalive = false) {
		let url = this._config.baseUrl + route;

		if (params) {
			const uri = new URL(url);
			for (const param in params) {
				if (!Object.prototype.hasOwnProperty.call(params, param))
					continue;
				if (params[param] === null)
					continue;
				if (params[param] === undefined)
					continue;
				uri.searchParams.set(param, params[param]);
			}
			url = uri.toString();
		}

		const init = {
			url: url,
			method: method,
			headers: headers || new window.Headers()
		};

		if (keepalive)
			init.keepalive = true;


		if (init.headers.get(`Content-Type`) === `multipart/form-data`) {
			// Form Data requires the automatically generated header for form data which includes the boundary property added on
			// If we delete it, the Fetch API will automatically generate the appropriate content type
			init.headers.delete(`Content-Type`);
			// We also want to set the body manually here as the form data, instead of json below
			init.body = body;
		} else {
			init.headers.append(`Content-Type`, `application/json`);
			if (body) {
				init.body = window.JSON.stringify(body);
			}
		}
		init.headers.append(`Accept`, `application/json`);

		const response = await window.fetch(url, init);

		const www_authenticate = response.headers.get(`www-authenticate`);
		if(www_authenticate && www_authenticate.startsWith(`Bearer`)) {
			const error = www_authenticate.match(/error="(?<error>[^"]+)"/)?.groups?.error;
			const description = www_authenticate.match(/error_description="(?<description>[^"]+)"/)?.groups?.description;
			let errorClass = TokenError;
			if(error === `invalid_token` && /token expired/.test(description)) errorClass = TokenExpiredError;
			throw new errorClass(error, description);
		}

		const contentType = response.headers.get(`content-type`);
		// Empty OK response
		if (contentType === null && response.ok) {
			return {};
		}

		const isJsonContent = (contentType && (contentType.includes(`application/json`) || contentType.includes(`application/problem+json`)));

		// Invalid Response
		if (!isJsonContent) {
			throw new ApiError(response.status, response.statusText, []);
		}

		const data = await response.json();

		if (this._config.validateData && (response.ok || response.status === 409)) {
			// Return a ValidationResponse if validateForms is enabled on configuration
			// Dont throw if there were errors, instead we display them visually
			data.ok = response.ok;
			return data;
		}

		// Error Response
		if (!response.ok) {
			throw new ApiError(response.status, response.statusText, data);
		}

		// OK + Object response
		return data;
	};
}

export default API;